import {
  Alert,
  Box,
  Button,
  Card,
  Checkbox,
  CircularProgress,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  TablePagination,
  TextField,
  Typography,
} from "@mui/material";
import * as React from "react";
import {
  BigCommerceProductFilter,
  ProductStatus,
} from "../../../../models/app/AppModels";
import {
  useGetNotSyncedWordpressProductsMutation,
  useMatchShopifyProductsWithDMSMutation,
} from "../../../../redux/reducer/features/Integration/DmsSetupSlice";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import SearchIcon from "@mui/icons-material/Search";
import { getStoreProductStatusChip } from "../../../../services/utilities/utility";
import CloseIcon from "@mui/icons-material/Close";
import { LinkInventoryDto } from "../../../../models/features/Integration/shopify";

interface ProductGridProps {
  handleNext: any;
  setSelectedProducts: (products: any[]) => void;
}

const WordpressProductGrid: React.FunctionComponent<ProductGridProps> = (
  props,
) => {
  const [products, setProducts] = React.useState<LinkInventoryDto[]>([]);
  const [productsCount, setProductsCount] = React.useState<number>(0);
  const [searchProduct, setSearchProduct] = React.useState("");
  const [selectedProducts, setSelectedProducts] = React.useState<
    LinkInventoryDto[]
  >([]);
  const [productStatus, setProductStatus] = React.useState("all");
  const [productsMutation, productsMutationResult] =
    useGetNotSyncedWordpressProductsMutation();
  const [matchProductsMutation, matchProductsMutationResult] =
    useMatchShopifyProductsWithDMSMutation();

  const [paging, setPaging] = React.useState<BigCommerceProductFilter>({
    pageIndex: 0,
    pageSize: 50,
    totalRecords: 0,
    quickFilterValues: [],
    items: [],
  });

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    setPaging({
      ...paging,
      pageIndex: newPage,
    });
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setPaging({
      ...paging,
      pageSize: parseInt(event.target.value, 10),
      pageIndex: 0,
    });
  };

  React.useEffect(() => {
    fetchProducts(paging);
  }, [paging, productStatus]);

  const fetchProducts = (pagingData: any) => {
    productsMutation({
      paging: {
        ...pagingData,
        productStatus:
          productStatus === "all"
            ? undefined
            : productStatus === "active"
              ? ProductStatus.Active
              : ProductStatus.Draft,
      },
    }).then((res: any) => {
      if (res && res.data && res.data.status === 200) {
        setProducts(res.data.entity.products);
        setProductsCount(res.data.entity.paging?.totalRecord || 0);
      }
    });
  };

  const debounce = (func: Function, delay: number) => {
    let timer: ReturnType<typeof setTimeout>;
    return function (this: any, ...args: any[]) {
      clearTimeout(timer);
      timer = setTimeout(() => func.apply(this, args), delay);
    };
  };

  const handleSearchTextChange = React.useCallback(
    debounce((value: any) => {
      setPaging({
        ...paging,
        quickFilterValues: value ? [value?.trim()] : [],
        pageIndex: 0,
      });
    }, 1000),
    [],
  );
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setSearchProduct(value);
    handleSearchTextChange(value);
  };
  const handleNextStep = () => {
    matchProductsMutation({ request: selectedProducts }).then((res: any) => {
      if (res && res.data) {
        if (res.data.status === 200) {
          props.setSelectedProducts(res.data.entityList);
          props.handleNext();
        }
      }
    });
  };

  const handleSelectProduct = (productId: any) => {
    setSelectedProducts((prev) => {
      if (prev.find((product) => product.externalProductId === productId)) {
        return prev.filter(
          (product) => product.externalProductId !== productId,
        );
      } else {
        const selectedProduct = products.find(
          (product) => product.externalProductId === productId,
        );
        return selectedProduct ? [...prev, selectedProduct] : prev;
      }
    });
  };

  const handleSelectAll = () => {
    const currentPageProductIds = products.map((p) => p.externalProductId);
    const currentPageSelectedCount = selectedProducts.filter((p) =>
      currentPageProductIds.includes(p.externalProductId),
    ).length;

    if (currentPageSelectedCount === products.length) {
      // Deselect all current page products
      setSelectedProducts((prev) =>
        prev.filter(
          (p) => !currentPageProductIds.includes(p.externalProductId),
        ),
      );
    } else {
      // Select all current page products (keeping previously selected from other pages)
      const currentPageProducts = products.filter(
        (p) =>
          !selectedProducts.some(
            (selected) => selected.externalProductId === p.externalProductId,
          ),
      );
      setSelectedProducts((prev) => [...prev, ...currentPageProducts]);
    }
  };
  const handleSelectChange = (event: any) => {
    setProductStatus(event.target.value);
  };
  // Computed values for current page selection state
  const currentPageProductIds = products.map((p) => p.variantId);
  const currentPageSelectedCount = selectedProducts.filter((p) =>
    currentPageProductIds.includes(p.variantId),
  ).length;
  const isCurrentPageAllSelected =
    currentPageSelectedCount === products.length && products.length > 0;
  const isCurrentPageIndeterminate =
    currentPageSelectedCount > 0 && currentPageSelectedCount < products.length;

  function Row({ row, key }: { row: LinkInventoryDto; key: any }) {
    return (
      <React.Fragment>
        <TableRow key={key}>
          <TableCell sx={{ padding: "7px" }}>
            <Checkbox
              checked={selectedProducts.some(
                (product) =>
                  product.externalProductId === row.externalProductId,
              )}
              onChange={() => handleSelectProduct(row.externalProductId)}
              onClick={(e) => e.stopPropagation()}
            />
          </TableCell>
          <TableCell sx={{ padding: "10px" }}>{row.vendor}</TableCell>
          <TableCell sx={{ padding: "10px" }}>{row.productTitle}</TableCell>
          <TableCell sx={{ padding: "10px" }}>{row.sku}</TableCell>
          <TableCell align="center" sx={{ padding: "10px" }}>
            {row?.price?.toFixed(2) ?? 0.0}
          </TableCell>
          <TableCell align="center" sx={{ padding: "10px" }}>
            {getStoreProductStatusChip(row.status)}
          </TableCell>
        </TableRow>
      </React.Fragment>
    );
  }
  return (
    <>
      <Box
        display={"flex"}
        justifyContent={"space-between"}
        alignItems={"center"}
        gap={2}
        p={0.5}
      >
        <Alert severity="info">
          Select Wordpress products to link with DMS products. TargetWeb will
          automatically try to find the best matches.
        </Alert>
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 2,
            alignItems: "center",
          }}
        >
          <Button
            disabled={
              selectedProducts.length === 0 ||
              matchProductsMutationResult.isLoading
            }
            size="small"
            variant="contained"
            onClick={handleNextStep}
            endIcon={
              matchProductsMutationResult.isLoading ? (
                <CircularProgress color="inherit" size={16} />
              ) : null
            }
          >
            Next
          </Button>
        </Box>
      </Box>
      <Card sx={{ borderColor: "rgba(0, 0, 0, 0.12)", border: "1px outset" }}>
        <Box
          display={"flex"}
          justifyContent={"flex-end"}
          gap={2}
          alignItems={"center"}
        >
          <Select
            value={productStatus}
            sx={{ width: "130px", mt: 0.5 }}
            onChange={handleSelectChange}
            variant="outlined"
            size="small"
          >
            <MenuItem selected value="all">
              All
            </MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="draft">Draft</MenuItem>
          </Select>
          <TextField
            id="search"
            name="search"
            placeholder="Search.."
            variant="standard"
            size="small"
            onChange={handleChange}
            value={searchProduct}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="primary" sx={{ fontSize: "20px" }} />
                </InputAdornment>
              ),
              endAdornment: searchProduct && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() =>
                      handleChange({
                        target: { value: "" },
                      } as React.ChangeEvent<HTMLInputElement>)
                    }
                    edge="end"
                  >
                    <CloseIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>
        <TableContainer component={Paper} sx={{ height: "59vh" }}>
          <Table
            aria-label="collapsible table"
            sx={{ borderCollapse: "separate" }}
          >
            <TableHead
              sx={{
                position: "sticky",
                zIndex: 100,
                top: 0,
                background: "white",
              }}
            >
              <TableRow sx={{ height: "2vh" }}>
                <TableCell sx={{ p: "7px", width: "1%" }}>
                  <Checkbox
                    checked={isCurrentPageAllSelected}
                    onChange={handleSelectAll}
                    indeterminate={isCurrentPageIndeterminate}
                  />
                </TableCell>
                <TableCell sx={{ padding: 0, width: "7%" }}>
                  <span style={{ color: "#E0E0E0" }}>|</span> Manufacturer
                </TableCell>
                <TableCell sx={{ padding: 0 }}>
                  <span style={{ color: "#E0E0E0" }}>|</span> Product Title
                </TableCell>
                <TableCell sx={{ padding: 0 }}>
                  <span style={{ color: "#E0E0E0" }}>|</span> SKU
                </TableCell>
                <TableCell align="center" sx={{ padding: 0, width: "7%" }}>
                  <span style={{ color: "#E0E0E0" }}>|</span> Price ($)
                </TableCell>
                <TableCell align="center" sx={{ padding: 0 }}>
                  <span style={{ color: "#E0E0E0" }}>|</span> Status
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {productsMutationResult.isLoading ? (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Box
                      justifyContent="center"
                      alignItems="center"
                      display="flex"
                      sx={{ height: "60vh" }}
                    >
                      <CircularProgress size="2.2rem" color="inherit" />
                    </Box>
                  </TableCell>
                </TableRow>
              ) : products && products.length > 0 ? (
                products.map((row: LinkInventoryDto) => (
                  <Row key={row.variantId} row={row} />
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Box
                      justifyContent="center"
                      alignItems="center"
                      display="flex"
                      sx={{ height: "60vh" }}
                    >
                      <Typography>No Rows to Show</Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            paddingLeft: 2,
            paddingRight: 1,
            borderTop: "1px solid rgba(224, 224, 224, 1)",
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          {/* Selected rows count - Left */}
          <Box sx={{ display: "flex", alignItems: "center", flex: "0 0 auto" }}>
            {selectedProducts.length > 0 && (
              <Typography variant="body2" sx={{ color: "rgba(0, 0, 0, 0.87)" }}>
                {selectedProducts.length}{" "}
                {selectedProducts.length === 1 ? "row" : "rows"} selected
              </Typography>
            )}
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", flex: "0 0 auto" }}>
            <TablePagination
              rowsPerPageOptions={[50, 100, 250]}
              count={productsCount}
              rowsPerPage={paging.pageSize}
              page={paging.pageIndex}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelDisplayedRows={({ from, to, count }) => {
                return `${from}–${to} of ${count}`;
              }}
              sx={{
                border: "none",
                "& .MuiTablePagination-toolbar": {
                  paddingLeft: 0,
                  minHeight: "auto",
                },
              }}
            />
          </Box>
        </Box>
      </Card>
    </>
  );
};

export default WordpressProductGrid;
